import Axios from 'axios'
import { styled } from '@mui/material';
import { Navigate, useLocation } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { FormTextField, SubmitButton } from '../../components';
import { ErrorResponse } from 'types/network';
import { Paper, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation } from 'react-query';
import { ROUTES } from 'constants/routes';
import { useMswManager } from 'lib/util/useMswManager';
import { HrznGatewayMswBuilder } from 'stories/HrznGatewayMswBuilder';

const Layout = styled('div')`
  max-width: 2048px;
  margin: 0 auto;
  padding: 0 30px;
`;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

interface FormValues {
  email: string,
  newPassword: string,
  confirmPassword: string
}

function ResetPassword() {
  const snackbar = useSnackbar()
  const query = useQuery();
  const { mutateAsync: resetPassword } = usePartnerUpSertExternalUserPasswordResetRequestFulfillment()

  const MyAccountSchema = Yup.object().shape({
    email: Yup.string().email().required('Required'),
    newPassword: Yup.string()
      .matches(
        // eslint-disable-next-line no-useless-escape
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
        'Password must contain more than 8 characters including: one uppercase, one lowercase, one number and one special case character.'
      )
      .required('Required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword'), undefined], 'Passwords must match')
      .required('Required')
  });

  function _checkIfNeedRedirect() {
    const redirectURL = query.get('forwardUrl');
    if (!redirectURL) return;
    window.location.href = redirectURL;
  }

  async function _handleSubmit(values: FormValues, { resetForm }) {
    const { email, newPassword } = values;
    await resetPassword({ email, newPassword })
      .then(() => {
        _checkIfNeedRedirect();
      })
      .catch(err => {
        snackbar.enqueueSnackbar((err as ErrorResponse).response?.data?.Error || '', { variant: 'error' })
      })
    resetForm();
  }

  const token = query.get('token');
  if (!token) {
    return <Navigate to="/" />;
  }

  return (
    <Layout>
      <Paper
        elevation={2}
        style={{
          maxWidth: '400px',
          margin: 'auto',
          marginTop: '20px',
          display: 'flex',
          flexDirection: 'column',
          padding: '40px'
        }}
      >
        <Typography variant="h4">Password Reset</Typography>
        <br />
        <Typography color="textSecondary" variant="body2">
          Please enter your email and new password.
        </Typography>

        <Formik<FormValues>
          onSubmit={_handleSubmit}
          initialValues={{
            email: '',
            newPassword: '',
            confirmPassword: ''
          }}
          validationSchema={MyAccountSchema}
          render={({ isValidating, isSubmitting }) => {
            return (
              <Form
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginTop: '15px'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <FormTextField
                    style={{ height: '76px' }}
                    idx="resetPassword-email"
                    name="email"
                    label="Email"
                    isRequired={true}
                  />
                  <FormTextField
                    style={{ minHeight: '76px' }}
                    idx="resetPassword-new-password"
                    name="newPassword"
                    label="New Password"
                    isRequired={true}
                    type="password"
                    autoComplete="new-password"
                  />
                  <FormTextField
                    style={{ height: '76px' }}
                    idx="resetPassword-confirm-password"
                    name="confirmPassword"
                    label="Confirm Password"
                    isRequired={true}
                    type="password"
                    autoComplete="new-password"
                  />
                  <SubmitButton
                    size="large"
                    id="resetPassword-submit"
                    loading={Boolean(isValidating || isSubmitting)}
                    disabled={Boolean(isValidating || isSubmitting)}
                  >
                    Submit
                  </SubmitButton>
                </div>
              </Form>
            );
          }}
        />
      </Paper>
    </Layout>
  );
}

export default ResetPassword;

interface PartnerUpSertExternalUserPasswordResetRequestFulfillmentPayload {
  email: string,
  newPassword: string
}

function usePartnerUpSertExternalUserPasswordResetRequestFulfillment() {
  const query = useQuery();
  const snackbar = useSnackbar()

  useMswManager(HrznGatewayMswBuilder.create().post(ROUTES.PartnerUpSertExternalUserPasswordResetRequestFulfillment, {
    data: 'success'
  }))

  return useMutation<unknown, ErrorResponse, PartnerUpSertExternalUserPasswordResetRequestFulfillmentPayload>(
    async ({ email, newPassword }) => {
      const { data } = await Axios({
        method: 'post',
        data: {
          email: email,
          uniqueIdentifier: query.get('token'),
          password: newPassword
        },
        params: {
          route: String(ROUTES.PartnerUpSertExternalUserPasswordResetRequestFulfillment)
        }
      })
      return data
    },
    {
      onError: (err) => {
        snackbar.enqueueSnackbar(err.response?.data?.Error || ROUTES.PartnerUpSertExternalUserPasswordResetRequestFulfillment, { variant: 'error' })
      },
      onSuccess: () => {
        snackbar.enqueueSnackbar('Password has been updated.', { variant: 'success' })
      }
    }
  )
}
